import React from 'react';
import Papa from 'papaparse';
import { useSelector } from 'react-redux';
import delay from 'lodash/delay';
import keyBy from 'lodash/keyBy';
import { axios } from '../../../../services/axios';
import { downloadCSV } from '../../../../utils/utils';
import appConfig from '../../../../config.json';
import Cookies from 'js-cookie';
import { PlatformEnums } from '../../../../utils/platforms';

export const CREATE_SUCCESS_MESSAGE = 'Ready to create';
export const CSV_TYPE = 'text/csv';
export const CSV_TYPE_FX = 'application/vnd.ms-excel';
export const CSV_TYPE_ERROR = 'File must be .csv type';

const INVALID_DATA_FILENAME = 'InvalidCreateData.csv';

const REQUIRED_FIELD_ERROR =
    'Missing required field(s): First Name, Last Name, and Email are required.';
const REQUIRED_FIELD_ERROR_RING_GROUP =
    'Missing required field(s): First Name (used as the Ring Group name) and Email are required.';
const EMAIL_FORMAT_ERROR = 'Invalid email format.';
const SITE_DNE_ERROR =
    'Site not found. Please check the site name matches exactly.';
const DUPLICATE_EMAIL_ERROR = 'Duplicate email found in this CSV upload.';
const PLATFORM_EXTENSION_FORMAT_ERROR =
    'Platform Extension must contain only numbers.';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PLATFORM_EXTENSION_REGEX = /^\d*$/;
const RING_GROUP_LABEL = 'Ring Group';

// 'Ring Group' (case-insensitive) maps to the RingGroup account type; anything
// else (including a blank column, for backwards compatibility with older
// templates) defaults to 'User'.
const normalizeAccountType = (value) =>
    (value || '').trim().toLowerCase() === RING_GROUP_LABEL.toLowerCase()
        ? 'RingGroup'
        : 'User';

const INITIAL_CHECKS = [
    { label: 'Received CSV', check: false, current: true },
    { label: 'Fetching Company Data', check: false, current: false },
    { label: 'Converting CSV Headers', check: false, current: false },
    { label: 'Verifying Required Fields', check: false, current: false },
    { label: 'Verifying Sites', check: false, current: false },
    { label: 'Checking for Duplicates', check: false, current: false },
];

export const DEFAULT_VERIFICATION_RESULT = {
    passed: { columns: [], data: [] },
    failed: { columns: [], data: [] },
};

const FIELDS_TO_EXPORT = {
    accountType: 'Account Type',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    site: 'Site',
    platformExtension: 'Platform Extension',
    errors: 'Errors',
};

export default function useCSVCreateUsers() {
    const [verificationChecks, setVerificationChecks] =
        React.useState(INITIAL_CHECKS);
    const [uploadData, setUploadData] = React.useState(null);
    const [payloads, setPayloads] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [csvLimit, setCSVLimit] = React.useState(false);
    const [verificationResult, setVerificationResult] = React.useState(
        DEFAULT_VERIFICATION_RESULT,
    );

    const { currentCompany } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);

    const markCheck = (i) => {
        delay(
            (index) => {
                setVerificationChecks((prev) => {
                    const next = [...prev];
                    if (next[index]) {
                        next[index].check = true;
                        if (index < next.length - 1)
                            next[index + 1].current = true;
                    }
                    return next;
                });
            },
            750,
            i,
        );
    };

    const handleReset = () => {
        setVerificationChecks(INITIAL_CHECKS.map((c) => ({ ...c })));
        setUploadData(null);
        setPayloads([]);
        setLoading(false);
        setError('');
        setCSVLimit(false);
        setVerificationResult(DEFAULT_VERIFICATION_RESULT);
    };

    const handleExportInvalidData = () => {
        const failed = verificationResult.failed.data;
        const rows = failed.map((row) =>
            Object.keys(FIELDS_TO_EXPORT).reduce((acc, key) => {
                if (key === 'errors' && Array.isArray(row[key])) {
                    acc[FIELDS_TO_EXPORT[key]] = row[key].join(' | ');
                } else if (key === 'accountType') {
                    acc[FIELDS_TO_EXPORT[key]] =
                        row[key] === 'RingGroup' ? RING_GROUP_LABEL : 'User';
                } else {
                    acc[FIELDS_TO_EXPORT[key]] = row[key] ?? '';
                }
                return acc;
            }, {}),
        );
        const res = Papa.unparse(rows);
        downloadCSV(res, INVALID_DATA_FILENAME);
    };

    const handleUploadUsersData = async (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;

        if (file.type !== CSV_TYPE && file.type !== CSV_TYPE_FX) {
            setError(CSV_TYPE_ERROR);
            return;
        }

        setLoading(true);
        setVerificationChecks(INITIAL_CHECKS.map((c) => ({ ...c })));

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                try {
                    if (results.data.length > appConfig.csvLimit) {
                        setCSVLimit(true);
                        setLoading(false);
                        return;
                    }

                    setUploadData(results.data);

                    // Step 0: Received CSV
                    markCheck(0);

                    // Step 1: Fetch company data (services + sites)
                    // Platform selection is disabled on this page (8x8-only),
                    // so the cookie is never set here — default to 8x8.
                    const platformId =
                        Cookies.get('selectedPlatformId') ||
                        PlatformEnums.EIGHTBYEIGHT;
                    let sitesLookupByName = {};
                    let serviceId = null;

                    try {
                        const servicesResponse = await axios.get(
                            `/Services/Company/${currentCompany}`,
                        );
                        const eightByEightService = servicesResponse.data.find(
                            (s) => s.serviceType === 'EightByEight',
                        );
                        serviceId = eightByEightService?.id;

                        if (serviceId) {
                            const sitesResponse = await axios.get(
                                `/Sites/${serviceId}`,
                                {
                                    headers: {
                                        'X-RequestScope': requiredScope,
                                    },
                                },
                            );
                            const sites = Array.isArray(sitesResponse.data)
                                ? sitesResponse.data
                                : [];
                            sitesLookupByName = keyBy(sites, (s) =>
                                (s.name || '').toLowerCase().trim(),
                            );
                        }
                    } catch (_err) {
                        // Sites fetch failed — site validation will reject all rows with a site
                    }

                    markCheck(1);

                    // Step 2: Convert CSV headers to internal shape
                    const converted = results.data.map((row) => ({
                        accountType: normalizeAccountType(row['Account Type']),
                        firstName: (row['First Name'] || '').trim(),
                        lastName: (row['Last Name'] || '').trim(),
                        email: (row['Email'] || '').trim(),
                        site: (row['Site'] || '').trim(),
                        platformExtension: (
                            row['Platform Extension'] || ''
                        ).trim(),
                    }));

                    markCheck(2);

                    // Step 3: Verify required fields
                    // Ring Group rows use First Name as the group's display
                    // name and do not require a Last Name.
                    const afterRequiredCheck = converted.map((row) => {
                        const errors = [];
                        const isRingGroup = row.accountType === 'RingGroup';

                        if (isRingGroup) {
                            if (!row.firstName || !row.email) {
                                errors.push(REQUIRED_FIELD_ERROR_RING_GROUP);
                            } else if (!EMAIL_REGEX.test(row.email)) {
                                errors.push(EMAIL_FORMAT_ERROR);
                            }
                        } else if (
                            !row.firstName ||
                            !row.lastName ||
                            !row.email
                        ) {
                            errors.push(REQUIRED_FIELD_ERROR);
                        } else if (!EMAIL_REGEX.test(row.email)) {
                            errors.push(EMAIL_FORMAT_ERROR);
                        }

                        if (
                            row.platformExtension &&
                            !PLATFORM_EXTENSION_REGEX.test(
                                row.platformExtension,
                            )
                        ) {
                            errors.push(PLATFORM_EXTENSION_FORMAT_ERROR);
                        }

                        return { ...row, errors };
                    });

                    markCheck(3);

                    // Step 4: Verify sites
                    const afterSiteCheck = afterRequiredCheck.map((row) => {
                        if (row.errors.length > 0) return row;
                        if (!row.site) return row; // site is optional; if blank, siteID = null
                        const siteKey = row.site.toLowerCase().trim();
                        if (!sitesLookupByName[siteKey]) {
                            return {
                                ...row,
                                errors: [...row.errors, SITE_DNE_ERROR],
                            };
                        }
                        return row;
                    });

                    markCheck(4);

                    // Step 5: Check for duplicate emails within the CSV
                    const emailCounts = {};
                    afterSiteCheck.forEach((row) => {
                        if (row.email) {
                            const key = row.email.toLowerCase();
                            emailCounts[key] = (emailCounts[key] || 0) + 1;
                        }
                    });
                    const afterDuplicateCheck = afterSiteCheck.map((row) => {
                        if (row.errors.length > 0) return row;
                        const key = row.email.toLowerCase();
                        if (emailCounts[key] > 1) {
                            return {
                                ...row,
                                errors: [...row.errors, DUPLICATE_EMAIL_ERROR],
                            };
                        }
                        return row;
                    });

                    markCheck(5);

                    // Partition into passed / failed
                    const passed = [];
                    const failed = [];
                    const builtPayloads = [];

                    afterDuplicateCheck.forEach((row) => {
                        if (row.errors.length > 0) {
                            failed.push({ ...row });
                            return;
                        }

                        const siteKey = row.site.toLowerCase().trim();
                        const siteObj = sitesLookupByName[siteKey];
                        const siteID = siteObj ? siteObj.id : null;
                        const isRingGroup = row.accountType === 'RingGroup';

                        const displayName = isRingGroup
                            ? row.firstName
                            : `${row.firstName} ${row.lastName}`.trim();

                        passed.push({
                            ...row,
                            verificationStatus: CREATE_SUCCESS_MESSAGE,
                        });

                        builtPayloads.push({
                            type: 'createPlatformUser',
                            payload: {
                                platformID: platformId,
                                companyID: currentCompany,
                                siteID,
                                givenName: isRingGroup ? '' : row.firstName,
                                surname: isRingGroup ? '' : row.lastName,
                                email: row.email,
                                displayName,
                                userTypeID: isRingGroup ? 'RingGroup' : 'User',
                                ...(isRingGroup
                                    ? {
                                          platformExtension:
                                              row.platformExtension,
                                      }
                                    : {}),
                            },
                            requiredScope,
                        });
                    });

                    setVerificationResult({
                        passed: { columns: [], data: passed },
                        failed: { columns: [], data: failed },
                    });
                    setPayloads(builtPayloads);
                } catch (err) {
                    setError(
                        err?.response?.data?.message ||
                            err?.message ||
                            'An error occurred while validating the CSV. Please try again.',
                    );
                }
                // Do NOT set loading=false here — loading stays true so the
                // validation-steps view remains visible until the user clicks "View Results".
            },
            error: (err) => {
                // Papa parse error means we can't proceed at all — reset loading
                setError(err?.message || 'Failed to parse CSV file.');
                setLoading(false);
            },
        });
    };

    return {
        handleUploadUsersData,
        uploadData,
        verificationChecks,
        loading,
        error,
        payloads,
        verificationResult,
        handleReset,
        handleExportInvalidData,
        csvLimit,
    };
}
